// Bilingual translations (English / Français) for SMART Centre Cameroon
// Add keys here when introducing new strings; both `en` and `fr` should mirror.

export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      smartechs: 'SMARTechs',
      trainings: 'Trainings',
      impact: 'Impact',
      news: 'News',
      contact: 'Contact',
      enrollNow: 'Enroll Now',
      languageToggleAria: 'Switch language',
    },
    footer: {
      tagline: 'Simple, Market-based, Affordable, Repairable Technologies for Water, Sanitation & Sustainable Livelihoods across Cameroon and Central Africa.',
      quickLinks: 'Quick Links',
      ourTrainings: 'Our Trainings',
      contactUs: 'Contact Us',
      limbeHQ: 'Limbe HQ',
      limbeAddress: 'Fako Division, SWR, Cameroon',
      tikoSatellite: 'Tiko Satellite Centre',
      tikoAddress: 'CDC Tiko – Towards SS Club',
      copyright: 'SMART Centre Cameroon Ltd. All rights reserved.',
      regLabel: 'Reg',
      trainings: {
        wash: 'WASH Technologies',
        agri: 'Agriculture & Farming',
        waste: 'Waste Management',
        food: 'Food Sciences',
      },
    },
    home: {
      hero: {
        title: 'SMART Centre Cameroon',
        subtitle: 'Simple, Affordable, Repairable Technologies for Water, Sanitation & Sustainable Livelihoods',
        description: 'Empowering communities across Cameroon — and Central Africa — with SMARTechs: self-supply WASH solutions from SMART Centre Group (Holland), EMAS (Bolivia/Germany) and TADEH (Spain).',
        enrollCta: 'Enroll in a Training',
        contactCta: 'Contact Us Now',
      },
      carousel: {
        eyebrow: 'SMARTechs in Action',
        title: 'Real solutions, built and used by communities',
        prev: 'Previous slide',
        next: 'Next slide',
        captions: [
          'Family irrigation through a Rope pump and low-cost drip tubes',
          'Community-led borehole installation with locally produced Rope pump',
          'Local artisans and partners at a completed Rope pump installation',
          'Drip irrigation in the field powered by a manual Rope pump',
          'Household water filters ensuring safe drinking water at home (HWTS)',
          'SCC CEO with Mr Hanks representing SMART Centres at an international exhibition',
          'SMART Centres booth showcasing low-cost climate-smart water technologies',
          'A community celebrating their newly hand-drilled borehole',
        ],
      },
      smart: {
        eyebrow: 'What does SMART mean?',
        title: 'The SMART approach, explained',
        S: 'Simple',
        M: 'Market-based',
        A: 'Affordable',
        R: 'Repairable',
        T: 'Technologies',
        explanation: 'SMARTechs are designed for **WASH** (Water, Sanitation & Hygiene) and now extend to **agriculture, waste management and food science**. Through a franchise model coordinated by **MetaMeta** (Netherlands) and partnerships with **EMAS** (Bolivia/Germany) and **TADEH** (Spain), we operate across the entire country of Cameroon via Regional Representatives — with future dissemination across Central Africa.',
        visionTitle: 'Our vision, mission & strategy',
        points: [
          { title: 'Simple, Market-based, Affordable & Repairable', description: 'Promote technologies and services that any local artisan or family can adopt and maintain.' },
          { title: 'Locally Produced', description: 'Focus on SMARTechs that can be manufactured locally — see also the SMARTechs catalogue.' },
          { title: 'Train the Local Private Sector', description: 'Build technical and business skills for a "profit-based sustainability" model.' },
          { title: 'Self-supply at Family Level', description: 'Promote household and family-level solutions that are affordable for lower-income families.' },
          { title: 'HWTS — Safe Water at Home', description: 'Promote Household Water Treatment & Safe Storage with a focus on household water filters.' },
          { title: 'Supply-chain for WASH', description: 'Assist local entrepreneurs in establishing a supply chain for affordable WASH technologies and products.' },
          { title: 'Branding & Promotion', description: 'Visibility and promotion of SMARTechs to grow demand and trust across communities.' },
        ],
      },
      about: {
        eyebrow: 'About Us',
        title: 'A franchise of the SMART Centre Group, rooted in Cameroon',
        p1: 'SMART Centre Cameroon Ltd (Registration: TPPRR/RC/LBE/2022/B/0198) operates a franchise model under the SMART Centre Group, coordinated by **MetaMeta**, a social enterprise based in the Netherlands, in partnership with **EMAS** (Bolivia/Germany) and **TADEH** (Spain).',
        p2: 'Headquartered in Limbe, Fako Division, SWR, Cameroon, with a satellite centre at Tiko Centre — CDC Tiko, Towards SS Club, SWR. Our coverage spans the entire country through Regional Representatives, with future expansion across Central Africa.',
        readMore: 'Learn More About Us',
      },
      smartechs: {
        eyebrow: 'Our Technologies',
        title: 'SMARTechs for Sustainable Living',
        cards: [
          { title: 'Hand-Drilled Boreholes', description: 'Affordable groundwater access using manual drilling techniques' },
          { title: 'Solar Water Heaters', description: 'Sustainable hot water solutions powered by solar energy' },
          { title: 'Filtration Systems', description: 'Water treatment and analysis for safe drinking water' },
          { title: 'Rainwater Harvesting', description: 'Collection systems for sustainable water supply' },
          { title: 'Odourless Latrines', description: 'Hygienic sanitation for households and communities' },
          { title: 'Affordable Irrigation', description: 'Cost-effective solutions for agricultural water needs' },
          { title: 'Solar Pump Systems', description: 'Renewable energy powered water pumping' },
          { title: 'Waste Recycling', description: 'Converting waste into valuable resources' },
        ],
        explore: 'Explore All Technologies',
      },
      trainings: {
        eyebrow: 'Education',
        title: 'Trainings & Courses',
        subtitle: 'Build practical skills for sustainable livelihoods with our specialized training programs',
        viewCourses: 'View Courses',
        viewAll: 'View All Trainings',
        categories: [
          { title: 'WASH Trainings', description: 'Water, Sanitation & Hygiene technologies and practices' },
          { title: 'Agriculture', description: 'Mushroom, Bee, Snail, Poultry, Fish farming and more' },
          { title: 'Waste Management', description: 'Recycling, Bio-digesters, and sustainable waste solutions' },
          { title: 'Food Sciences', description: 'Preservation, processing, safety and nutrition' },
        ],
      },
      benefits: {
        eyebrow: 'Our Approach',
        title: 'Why Choose SMART Centre?',
        items: [
          { title: 'Affordable', description: 'Low-cost technologies using locally available materials' },
          { title: 'Self-Constructible', description: 'Community members can build and install themselves' },
          { title: 'Locally Repairable', description: 'Easy maintenance with local skills and resources' },
          { title: 'SDG6 Focused', description: 'Aligned with UN Sustainable Development Goals' },
        ],
      },
      gallery: {
        eyebrow: 'Our Impact',
        title: 'Making a Difference',
        cta: 'View Full Gallery',
      },
      testimonials: {
        eyebrow: 'Testimonials',
        title: 'What Our Trainees Say',
        items: [
          { quote: 'The borehole training changed my life. I now provide clean water to my entire village.', author: 'Emmanuel N.', location: 'Buea' },
          { quote: "SMART Centre's agricultural training helped me start a successful mushroom farm.", author: 'Grace M.', location: 'Limbe' },
        ],
      },
      cta: {
        title: 'Ready to Make a Difference?',
        subtitle: 'Join our trainings and gain the skills to transform your community with sustainable technologies.',
        enroll: 'Enroll Today',
        contact: 'Get in Touch',
      },
    },
    about: {
      hero: {
        eyebrow: 'About Us',
        title: 'SMART Centre Cameroon Ltd',
        subtitle: 'A franchise of the SMART Centre Group, disseminating SMARTechs across Cameroon and Central Africa.',
      },
      intro: {
        p1: 'The **SMARTechs** (Simple, Market-based, Affordable, Repairable Technologies) are a self-supply WASH technology family developed by **SMART Centre Group** (Holland), **EMAS Technologies** (Bolivia/Germany) and **TADEH** (Spain). SMART Centre Cameroon Ltd is authorised to disseminate them across the entire country and, in future, across Central Africa.',
        p2: 'SMART Centre Cameroon Ltd operates a franchise model under SMART Centre Group, coordinated by **MetaMeta**, a social enterprise based in the Netherlands. Our headquarters is in **Limbe, Fako Division, SWR, Cameroon**, with a satellite centre at **Tiko Centre — CDC Tiko, Towards SS Club, SWR, Cameroon**. We operate nationwide through Regional Representatives.',
        p3: 'Beyond WASH, SMART Centre Cameroon offers specialised technological trainings and services in **Agriculture, Waste Management & Recycling, Food Sciences, Health & Social Care, Film Production and Information Technology**.',
        established: 'Established',
        establishedNote: 'in Cameroon (Reg. TPPRR/RC/LBE/2022/B/0198)',
      },
      mission: {
        title: 'Our Mission',
        body: 'To empower communities across Cameroon and Central Africa with affordable, locally-repairable technologies that provide sustainable access to clean water, improved sanitation, and livelihood opportunities through practical skills training.',
      },
      vision: {
        title: 'Our Vision',
        body: 'A Cameroon — and a Central Africa — where every community has access to clean water, proper sanitation, and the skills needed for sustainable livelihoods, built and maintained by community members themselves.',
      },
      strategyTitle: 'Our Strategy',
      strategySubtitle: 'Seven principles that guide every project we deliver.',
      services: {
        eyebrow: 'What We Offer',
        title: 'Our Training Areas',
        items: [
          'Water Sanitation and Hygiene (WASH)',
          'Agriculture',
          'Waste Management and Recycling',
          'Food Sciences',
          'Health and Social Care',
          'Film Production',
          'Information Technology',
        ],
      },
      partners: {
        eyebrow: 'Our Network',
        title: 'International Partners',
        list: [
          { name: 'SMART Centre Group', location: 'Coordinated by MetaMeta — Netherlands' },
          { name: 'EMAS Technologies', location: 'Bolivia / Germany' },
          { name: 'TADEH', location: 'Spain' },
        ],
      },
      locations: {
        eyebrow: 'Find Us',
        title: 'Our Locations',
        hq: 'Headquarters – Limbe',
        hqAddr: 'Fako Division\nSouth West Region\nCameroon',
        satellite: 'Satellite Centre – Tiko',
        satelliteAddr: 'CDC Tiko – Towards SS Club\nSouth West Region\nCameroon',
      },
      cta: {
        title: 'Join Our Mission',
        body: 'Partner with us to bring sustainable solutions to communities across Cameroon and Central Africa.',
        button: 'Get in Touch',
      },
    },
    smartechs: {
      hero: {
        eyebrow: 'Our Technologies',
        title: 'SMARTechs',
        subtitle: 'Simple, Market-based, Affordable, Repairable Technologies for sustainable Water, Sanitation, Hygiene and Livelihood solutions.',
      },
      principle: {
        eyebrow: 'The SMART Approach',
        title: 'What makes SMARTechs special?',
        body: 'SMARTechs are technologies and services that any local artisan or family can adopt, build and maintain. They are produced locally where possible, promoted as **self-supply** at family level, and supported by a local private-sector supply chain — so safe water, sanitation and hygiene reach the people who need them most.',
        benefits: [
          'Cheap and affordable for low-income communities',
          'Made from locally available materials',
          'Self-maintainable by community members',
          'Self-constructible with basic training',
          'Promote HWTS (Household Water Treatment & Safe Storage)',
          'Aligned with SDG 6 — water and sanitation for all',
        ],
      },
      grid: {
        title: 'Our Technology Solutions',
        subtitle: 'Explore our range of sustainable technologies designed for community implementation.',
      },
      cards: [
        {
          title: 'Hand-Drilled Boreholes & Hand-Dug Wells',
          description: 'Access groundwater affordably using manual drilling techniques that communities can implement themselves.',
          features: ['Low-cost drilling equipment', 'Community-operated technology', 'Suitable for various soil types', 'Easy maintenance and repair'],
        },
        {
          title: 'Solar Water Heaters & Solar Pumps',
          description: 'Harness renewable solar energy for hot water and water pumping needs without ongoing fuel costs.',
          features: ['Zero operational costs', 'Environmentally friendly', 'Long lifespan (20+ years)', 'Minimal maintenance required'],
        },
        {
          title: 'Filtration Systems, Water Treatment & Analysis',
          description: 'Ensure safe drinking water through household water filters, treatment and water-quality testing.',
          features: ['Household and community scale', 'Water quality testing', 'Multiple filtration options', 'Production of disinfectants'],
        },
        {
          title: 'Rainwater Harvesting',
          description: 'Capture and store rainwater for domestic use, especially valuable in areas with seasonal rainfall.',
          features: ['Roof catchment systems', 'Underground storage tanks', 'First-flush diverters', 'Gravity-fed distribution'],
        },
        {
          title: 'Odourless Latrines',
          description: 'Hygienic sanitation solutions for households and communities that eliminate odour and flies.',
          features: ['Ventilated improved pit design', 'Pour-flush options', 'Community and household scale', 'Easy construction methods'],
        },
        {
          title: 'Affordable Irrigation Schemes',
          description: 'Cost-effective water management solutions for agricultural production.',
          features: ['Drip irrigation systems', 'Treadle pumps', 'Rope-and-washer pumps', 'Water-efficient techniques'],
        },
        {
          title: 'Renewable Energy Solar Packages',
          description: 'Complete solar solutions for household and small business energy needs.',
          features: ['Solar home systems', 'Solar lighting packages', 'Phone charging stations', 'Small appliance power'],
        },
        {
          title: 'Waste Recycling Solutions',
          description: 'Convert waste into valuable resources through various recycling and repurposing technologies.',
          features: ['Plastic waste conversion', 'Organic waste composting', 'Bio-digester systems', 'Construction materials from waste'],
        },
      ],
      cta: {
        title: 'Learn to build these technologies',
        body: 'Join our training programs and gain hands-on skills to implement SMARTechs in your community.',
        button: 'View Our Trainings',
      },
    },
  },

  fr: {
    nav: {
      home: 'Accueil',
      about: 'À propos',
      smartechs: 'SMARTechs',
      trainings: 'Formations',
      impact: 'Impact',
      news: 'Actualités',
      contact: 'Contact',
      enrollNow: 'S’inscrire',
      languageToggleAria: 'Changer de langue',
    },
    footer: {
      tagline: 'Technologies Simples, Adaptées au marché, Abordables et Réparables pour l’Eau, l’Assainissement et des Moyens de Subsistance Durables au Cameroun et en Afrique centrale.',
      quickLinks: 'Liens rapides',
      ourTrainings: 'Nos formations',
      contactUs: 'Nous contacter',
      limbeHQ: 'Siège — Limbe',
      limbeAddress: 'Fako Division, Sud-Ouest, Cameroun',
      tikoSatellite: 'Centre satellite de Tiko',
      tikoAddress: 'CDC Tiko – vers SS Club',
      copyright: 'SMART Centre Cameroon Ltd. Tous droits réservés.',
      regLabel: 'Reg',
      trainings: {
        wash: 'Technologies WASH',
        agri: 'Agriculture & élevage',
        waste: 'Gestion des déchets',
        food: 'Sciences alimentaires',
      },
    },
    home: {
      hero: {
        title: 'SMART Centre Cameroun',
        subtitle: 'Technologies Simples, Abordables et Réparables pour l’Eau, l’Assainissement et des Moyens de Subsistance Durables',
        description: 'Au service des communautés du Cameroun — et de l’Afrique centrale — avec les SMARTechs : solutions WASH d’auto-approvisionnement de SMART Centre Group (Pays-Bas), EMAS (Bolivie/Allemagne) et TADEH (Espagne).',
        enrollCta: 'S’inscrire à une formation',
        contactCta: 'Nous contacter',
      },
      carousel: {
        eyebrow: 'Les SMARTechs en action',
        title: 'Des solutions concrètes, construites et utilisées par les communautés',
        prev: 'Diapositive précédente',
        next: 'Diapositive suivante',
        captions: [
          'Irrigation familiale grâce à une pompe à corde et à des tuyaux goutte-à-goutte abordables',
          'Installation d’un forage communautaire avec une pompe à corde produite localement',
          'Artisans locaux et partenaires devant une installation de pompe à corde terminée',
          'Irrigation goutte-à-goutte au champ alimentée par une pompe à corde manuelle',
          'Filtres à eau domestiques garantissant une eau potable saine à la maison (HWTS)',
          'Le DG de SCC avec M. Hanks représentant les SMART Centres à une exposition internationale',
          'Stand SMART Centres présentant des technologies de l’eau abordables et adaptées au climat',
          'Une communauté célébrant son nouveau forage manuel',
        ],
      },
      smart: {
        eyebrow: 'Que signifie SMART ?',
        title: 'L’approche SMART, expliquée',
        S: 'Simple',
        M: 'Adaptée au marché',
        A: 'Abordable',
        R: 'Réparable',
        T: 'Technologie',
        explanation: 'Les SMARTechs sont conçues pour le **WASH** (Eau, Assainissement & Hygiène) et s’étendent désormais à **l’agriculture, la gestion des déchets et la science des aliments**. Grâce à un modèle de franchise coordonné par **MetaMeta** (Pays-Bas) et à des partenariats avec **EMAS** (Bolivie/Allemagne) et **TADEH** (Espagne), nous intervenons sur l’ensemble du territoire camerounais via nos représentants régionaux — avec une diffusion future en Afrique centrale.',
        visionTitle: 'Notre vision, notre mission et notre stratégie',
        points: [
          { title: 'Simple, Adaptée au marché, Abordable & Réparable', description: 'Promouvoir des technologies et des services que tout artisan ou famille peut adopter et entretenir.' },
          { title: 'Production locale', description: 'Privilégier des SMARTechs fabriquées localement — voir le catalogue SMARTechs.' },
          { title: 'Former le secteur privé local', description: 'Renforcer les compétences techniques et entrepreneuriales pour une "durabilité par le profit".' },
          { title: 'Auto-approvisionnement familial', description: 'Promouvoir des solutions au niveau familial, abordables pour les ménages à faibles revenus.' },
          { title: 'HWTS — Eau potable au foyer', description: 'Promouvoir le traitement et le stockage sûr de l’eau à domicile, avec un focus sur les filtres ménagers.' },
          { title: 'Chaîne d’approvisionnement WASH', description: 'Aider les entrepreneurs locaux à mettre en place une chaîne d’approvisionnement pour des technologies WASH abordables.' },
          { title: 'Image de marque & promotion', description: 'Visibilité et promotion des SMARTechs pour développer la demande et la confiance dans les communautés.' },
        ],
      },
      about: {
        eyebrow: 'À propos',
        title: 'Une franchise du SMART Centre Group, ancrée au Cameroun',
        p1: 'SMART Centre Cameroon Ltd (RCCM : TPPRR/RC/LBE/2022/B/0198) opère selon un modèle de franchise du SMART Centre Group, coordonné par **MetaMeta**, une entreprise sociale basée aux Pays-Bas, en partenariat avec **EMAS** (Bolivie/Allemagne) et **TADEH** (Espagne).',
        p2: 'Notre siège est à Limbé, département du Fako, région du Sud-Ouest, Cameroun, avec un centre satellite à Tiko Centre — CDC Tiko, vers SS Club. Notre couverture s’étend à tout le pays via nos représentants régionaux, avec une expansion future en Afrique centrale.',
        readMore: 'En savoir plus sur nous',
      },
      smartechs: {
        eyebrow: 'Nos technologies',
        title: 'Des SMARTechs pour une vie durable',
        cards: [
          { title: 'Forages manuels', description: 'Accès abordable aux eaux souterraines grâce à des techniques de forage manuel' },
          { title: 'Chauffe-eau solaires', description: 'Solutions d’eau chaude durables alimentées par l’énergie solaire' },
          { title: 'Systèmes de filtration', description: 'Traitement et analyse de l’eau pour une eau de boisson saine' },
          { title: 'Récupération d’eau de pluie', description: 'Systèmes de collecte pour un approvisionnement durable' },
          { title: 'Latrines sans odeur', description: 'Assainissement hygiénique pour les ménages et les communautés' },
          { title: 'Irrigation abordable', description: 'Solutions économiques pour les besoins en eau agricoles' },
          { title: 'Pompes solaires', description: 'Pompage de l’eau alimenté par énergie renouvelable' },
          { title: 'Recyclage des déchets', description: 'Transformer les déchets en ressources de valeur' },
        ],
        explore: 'Voir toutes les technologies',
      },
      trainings: {
        eyebrow: 'Éducation',
        title: 'Formations & cours',
        subtitle: 'Acquérir des compétences pratiques pour des moyens de subsistance durables grâce à nos programmes spécialisés',
        viewCourses: 'Voir les cours',
        viewAll: 'Voir toutes les formations',
        categories: [
          { title: 'Formations WASH', description: 'Technologies et pratiques d’eau, d’assainissement et d’hygiène' },
          { title: 'Agriculture', description: 'Champignons, abeilles, escargots, volaille, pisciculture et plus' },
          { title: 'Gestion des déchets', description: 'Recyclage, bio-digesteurs et solutions durables' },
          { title: 'Sciences alimentaires', description: 'Conservation, transformation, sécurité et nutrition' },
        ],
      },
      benefits: {
        eyebrow: 'Notre approche',
        title: 'Pourquoi choisir SMART Centre ?',
        items: [
          { title: 'Abordable', description: 'Technologies à faible coût utilisant des matériaux disponibles localement' },
          { title: 'Auto-constructible', description: 'Les membres de la communauté peuvent construire et installer eux-mêmes' },
          { title: 'Réparable localement', description: 'Entretien facile avec les compétences et ressources locales' },
          { title: 'Aligné sur l’ODD 6', description: 'En phase avec les Objectifs de développement durable de l’ONU' },
        ],
      },
      gallery: {
        eyebrow: 'Notre impact',
        title: 'Faire la différence',
        cta: 'Voir toute la galerie',
      },
      testimonials: {
        eyebrow: 'Témoignages',
        title: 'Ce que disent nos stagiaires',
        items: [
          { quote: 'La formation au forage a changé ma vie. Je fournis désormais de l’eau potable à tout mon village.', author: 'Emmanuel N.', location: 'Buéa' },
          { quote: 'La formation agricole de SMART Centre m’a permis de lancer une exploitation de champignons prospère.', author: 'Grace M.', location: 'Limbé' },
        ],
      },
      cta: {
        title: 'Prêt(e) à faire la différence ?',
        subtitle: 'Rejoignez nos formations et acquérez les compétences pour transformer votre communauté avec des technologies durables.',
        enroll: 'S’inscrire aujourd’hui',
        contact: 'Nous contacter',
      },
    },
    about: {
      hero: {
        eyebrow: 'À propos',
        title: 'SMART Centre Cameroon Ltd',
        subtitle: 'Une franchise du SMART Centre Group qui diffuse les SMARTechs à travers le Cameroun et l’Afrique centrale.',
      },
      intro: {
        p1: 'Les **SMARTechs** (Technologies Simples, Adaptées au marché, Abordables, Réparables) forment une famille de technologies WASH d’auto-approvisionnement développée par **SMART Centre Group** (Pays-Bas), **EMAS Technologies** (Bolivie/Allemagne) et **TADEH** (Espagne). SMART Centre Cameroon Ltd est autorisée à les diffuser sur l’ensemble du pays et, à terme, dans toute l’Afrique centrale.',
        p2: 'SMART Centre Cameroon Ltd opère selon un modèle de franchise du SMART Centre Group, coordonné par **MetaMeta**, une entreprise sociale basée aux Pays-Bas. Notre siège est à **Limbé, département du Fako, région du Sud-Ouest, Cameroun**, avec un centre satellite à **Tiko Centre — CDC Tiko, vers SS Club, Cameroun**. Nous intervenons à l’échelle nationale via nos représentants régionaux.',
        p3: 'Au-delà du WASH, SMART Centre Cameroon offre des formations et services technologiques spécialisés en **Agriculture, Gestion et Recyclage des déchets, Sciences alimentaires, Santé et action sociale, Production audiovisuelle et Technologies de l’information**.',
        established: 'Création',
        establishedNote: 'au Cameroun (RCCM TPPRR/RC/LBE/2022/B/0198)',
      },
      mission: {
        title: 'Notre mission',
        body: 'Donner aux communautés du Cameroun et de l’Afrique centrale les moyens d’accéder durablement à l’eau potable, à un assainissement amélioré et à des opportunités de subsistance, grâce à des technologies abordables et réparables localement et à des formations pratiques.',
      },
      vision: {
        title: 'Notre vision',
        body: 'Un Cameroun — et une Afrique centrale — où chaque communauté a accès à l’eau potable, à un assainissement adéquat et aux compétences nécessaires pour des moyens de subsistance durables, construits et entretenus par les communautés elles-mêmes.',
      },
      strategyTitle: 'Notre stratégie',
      strategySubtitle: 'Sept principes qui guident chacun de nos projets.',
      services: {
        eyebrow: 'Ce que nous offrons',
        title: 'Nos domaines de formation',
        items: [
          'Eau, assainissement et hygiène (WASH)',
          'Agriculture',
          'Gestion et recyclage des déchets',
          'Sciences alimentaires',
          'Santé et action sociale',
          'Production audiovisuelle',
          'Technologies de l’information',
        ],
      },
      partners: {
        eyebrow: 'Notre réseau',
        title: 'Partenaires internationaux',
        list: [
          { name: 'SMART Centre Group', location: 'Coordonné par MetaMeta — Pays-Bas' },
          { name: 'EMAS Technologies', location: 'Bolivie / Allemagne' },
          { name: 'TADEH', location: 'Espagne' },
        ],
      },
      locations: {
        eyebrow: 'Nous trouver',
        title: 'Nos implantations',
        hq: 'Siège – Limbé',
        hqAddr: 'Département du Fako\nRégion du Sud-Ouest\nCameroun',
        satellite: 'Centre satellite – Tiko',
        satelliteAddr: 'CDC Tiko – vers SS Club\nRégion du Sud-Ouest\nCameroun',
      },
      cta: {
        title: 'Rejoignez notre mission',
        body: 'Devenez partenaire pour apporter des solutions durables aux communautés du Cameroun et d’Afrique centrale.',
        button: 'Nous contacter',
      },
    },
    smartechs: {
      hero: {
        eyebrow: 'Nos technologies',
        title: 'SMARTechs',
        subtitle: 'Technologies Simples, Adaptées au marché, Abordables et Réparables pour des solutions durables d’eau, d’assainissement, d’hygiène et de subsistance.',
      },
      principle: {
        eyebrow: 'L’approche SMART',
        title: 'Pourquoi les SMARTechs sont uniques ?',
        body: 'Les SMARTechs sont des technologies et services que tout artisan ou famille peut adopter, construire et entretenir. Elles sont produites localement quand c’est possible, promues comme **auto-approvisionnement** au niveau familial et soutenues par une chaîne d’approvisionnement privée — pour que l’eau, l’assainissement et l’hygiène atteignent ceux qui en ont le plus besoin.',
        benefits: [
          'Bon marché et abordable pour les communautés à faibles revenus',
          'Fabriquées avec des matériaux disponibles localement',
          'Entretenues par les membres de la communauté',
          'Construites soi-même avec une formation de base',
          'Promeuvent le HWTS (traitement et stockage sûr de l’eau à domicile)',
          'Alignées sur l’ODD 6 — eau et assainissement pour tous',
        ],
      },
      grid: {
        title: 'Nos solutions technologiques',
        subtitle: 'Découvrez notre gamme de technologies durables conçues pour la mise en œuvre communautaire.',
      },
      cards: [
        {
          title: 'Forages manuels & puits creusés à la main',
          description: 'Accédez aux eaux souterraines à moindre coût grâce à des techniques de forage manuel que les communautés peuvent mettre en œuvre.',
          features: ['Équipement de forage à faible coût', 'Technologie opérée par la communauté', 'Adaptée à divers types de sols', 'Entretien et réparation simples'],
        },
        {
          title: 'Chauffe-eau et pompes solaires',
          description: 'Exploitez l’énergie solaire renouvelable pour l’eau chaude et le pompage, sans coût continu de carburant.',
          features: ['Coûts d’exploitation nuls', 'Respectueux de l’environnement', 'Longue durée de vie (20+ ans)', 'Entretien minimal requis'],
        },
        {
          title: 'Filtration, traitement et analyse de l’eau',
          description: 'Garantir une eau potable sûre grâce aux filtres ménagers, au traitement et au contrôle de la qualité.',
          features: ['Échelle ménagère et communautaire', 'Tests de qualité de l’eau', 'Plusieurs options de filtration', 'Production de désinfectants'],
        },
        {
          title: 'Récupération d’eau de pluie',
          description: 'Capter et stocker l’eau de pluie pour l’usage domestique, surtout dans les zones à pluviométrie saisonnière.',
          features: ['Systèmes de captage de toiture', 'Citernes souterraines', 'Déviateurs de premières eaux', 'Distribution gravitaire'],
        },
        {
          title: 'Latrines sans odeur',
          description: 'Solutions d’assainissement hygiéniques pour ménages et communautés, sans odeur ni mouches.',
          features: ['Conception VIP (fosse améliorée ventilée)', 'Options à chasse manuelle', 'Échelle ménagère et communautaire', 'Méthodes de construction simples'],
        },
        {
          title: 'Systèmes d’irrigation abordables',
          description: 'Solutions de gestion de l’eau économiques pour la production agricole.',
          features: ['Irrigation goutte-à-goutte', 'Pompes à pédale', 'Pompes à corde et rondelle', 'Techniques économes en eau'],
        },
        {
          title: 'Kits solaires d’énergie renouvelable',
          description: 'Solutions solaires complètes pour les besoins énergétiques des ménages et petites entreprises.',
          features: ['Kits solaires domestiques', 'Éclairage solaire', 'Stations de recharge de téléphones', 'Alimentation pour petits appareils'],
        },
        {
          title: 'Solutions de recyclage des déchets',
          description: 'Transformer les déchets en ressources grâce à diverses technologies de recyclage et de réemploi.',
          features: ['Conversion des plastiques', 'Compostage des déchets organiques', 'Bio-digesteurs', 'Matériaux de construction issus des déchets'],
        },
      ],
      cta: {
        title: 'Apprenez à construire ces technologies',
        body: 'Rejoignez nos formations et acquérez les compétences pratiques pour mettre en œuvre les SMARTechs dans votre communauté.',
        button: 'Voir nos formations',
      },
    },
  },
};
